/**
 * Reclaim Biohacking · Sleep — Phase 1 MVP
 *
 * Sections: Overview dashboard · Daily Protocol · Checklist · Installations · Tools
 * Route: /biohacking/sleep
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Moon, Sun, Sunrise, Sunset, Bed, Sparkles, Check,
  Volume2, ShoppingBag, ChevronRight, Loader2, Play, Pause, Headphones,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const BLOCK_ICON = { morning: Sunrise, daytime: Sun, evening: Sunset, bedroom: Bed };

export default function BiohackingSleep() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview"); // overview | protocol | checklist | installations | tools

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#02050B] via-[#04070F] to-[#020308] text-white">
      <header className="px-5 pt-5 pb-3 flex items-center gap-3 sticky top-0 backdrop-blur bg-[#02050B]/80 z-10">
        {/* Back is handled globally by <GlobalBackButton/>. */}
        <div className="flex items-center gap-2 ml-12">
          <Moon size={20} className="text-indigo-300" />
          <h1 className="text-xl tracking-wide" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Sleep Biohacking
          </h1>
        </div>
      </header>

      <nav className="px-5 mb-5 flex gap-1.5 overflow-x-auto no-scrollbar">
        {[
          { id: "overview",      label: "Overview" },
          { id: "protocol",      label: "Protocol" },
          { id: "checklist",     label: "Checklist" },
          { id: "installations", label: "Sleep Installations" },
          { id: "tools",         label: "Tools" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`sleep-tab-${t.id}`}
            className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap transition ${
              tab === t.id ? "bg-indigo-500/25 text-indigo-100 ring-1 ring-indigo-400/40"
                           : "bg-white/[0.03] text-white/55 ring-1 ring-white/8 hover:text-white/85"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main className="px-5 pb-24 max-w-2xl mx-auto">
        {tab === "overview" && <OverviewPanel />}
        {tab === "protocol" && <ProtocolPanel />}
        {tab === "checklist" && <ChecklistPanel />}
        {tab === "installations" && <InstallationsPanel />}
        {tab === "tools" && <ToolsPanel />}
      </main>
    </div>
  );
}

// ── Overview ────────────────────────────────────────────────
function OverviewPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(false);
  // One-tap clone state for the Day/Night sleep journeys. The sleep
  // blueprints are fully curated already (theme, music, brainwaves,
  // voice speed, all 6 phases of affirmations) — there is nothing
  // for the user to tweak. So tapping a CTA renders the journey in
  // place rather than dumping the user into the generic My Journeys
  // wizard where they'd see Performance themes (Confidence, Focus,
  // Peak Performance…) that have nothing to do with sleep.
  const [preparing, setPreparing] = useState(null); // null | 'day' | 'night'
  const refresh = useCallback(() => {
    fetch(`${API}/api/biohacking/sleep/overview`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setData).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const launchSleepJourney = async (which) => {
    // which = 'day' | 'night'
    if (preparing) return;
    setPreparing(which);
    try {
      const bpId = which === "day" ? "bp_sleep_day" : "bp_sleep_night";
      const r = await fetch(`${API}/api/audio-journeys/blueprints/${bpId}/clone`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: "{}",
      });
      if (!r.ok) throw new Error("clone failed");
      // Land in My Journeys where the freshly-cloned card will show
      // its render progress and become playable in 60–180s. The list
      // is sleep-context-safe (just shows the user's own journeys,
      // no Performance theme picker).
      navigate("/my-journeys");
    } catch (e) {
      console.error("[sleep-journey] clone failed:", e);
      setPreparing(null);
    }
  };

  if (!data) return <Skel />;
  const tiles = data.tiles || {};
  const recovery = tiles.recovery_score || {};
  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500/15 via-violet-500/10 to-transparent ring-1 ring-indigo-400/25 p-6 text-center" data-testid="sleep-score-card">
        <div className="text-white/50 text-xs uppercase tracking-[0.3em]">Sleep Score</div>
        <div className="text-6xl font-light my-2 text-indigo-100">{data.sleep_score}</div>
        <div className="text-white/45 text-xs">{data.tracked_days} of last 30 days tracked</div>
      </div>

      {/* ── Day / Night Sleep Journey CTAs ────────────────────────
          One-tap clone — the sleep blueprints are fully baked so we
          render in place rather than dropping the user into the
          generic My Journeys wizard. Keeps the entire flow in
          "preparing for sleep" context. */}
      <div className="grid grid-cols-2 gap-3" data-testid="sleep-journey-ctas">
        <button
          onClick={() => launchSleepJourney("day")}
          disabled={!!preparing}
          data-testid="sleep-journey-day"
          className="text-left rounded-2xl p-4 ring-1 ring-amber-400/25 bg-gradient-to-br from-amber-500/15 via-amber-400/8 to-transparent hover:ring-amber-300/45 transition group disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2 text-amber-200/90">
            <Sun size={16} />
            <span className="text-[10px] uppercase tracking-[0.22em]">Daytime</span>
          </div>
          <div className="mt-2 text-xl text-amber-50/95 font-light" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Sleep — Day
          </div>
          <div className="mt-1 text-amber-100/55 text-xs italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Install the identity of a deep sleeper while you&apos;re awake.
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-amber-200/70 text-[11px]">
            {preparing === "day" ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span>Preparing your installation…</span>
              </>
            ) : (
              <>
                <Headphones size={12} /> 20 min · Day pacing
                <ChevronRight size={14} className="ml-auto opacity-60 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </div>
        </button>

        <button
          onClick={() => launchSleepJourney("night")}
          disabled={!!preparing}
          data-testid="sleep-journey-night"
          className="text-left rounded-2xl p-4 ring-1 ring-indigo-400/25 bg-gradient-to-br from-indigo-500/20 via-violet-500/8 to-transparent hover:ring-indigo-300/50 transition group disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <div className="flex items-center gap-2 text-indigo-200/90">
            <Moon size={16} />
            <span className="text-[10px] uppercase tracking-[0.22em]">Bedtime</span>
          </div>
          <div className="mt-2 text-xl text-indigo-50/95 font-light" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Sleep — Night
          </div>
          <div className="mt-1 text-indigo-100/55 text-xs italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            The body sinks. The day dissolves.
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-indigo-200/70 text-[11px]">
            {preparing === "night" ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span>Preparing your installation…</span>
              </>
            ) : (
              <>
                <Headphones size={12} /> 30 min · Slow pacing
                <ChevronRight size={14} className="ml-auto opacity-60 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </div>
        </button>
      </div>

      {/* ── Recovery Score (subjective morning rating) ──────── */}
      <button
        onClick={() => setRating(true)}
        data-testid="sleep-recovery-tile"
        className="w-full text-left rounded-xl bg-gradient-to-br from-emerald-500/12 via-teal-500/6 to-transparent ring-1 ring-emerald-400/25 hover:ring-emerald-300/45 transition p-4 flex items-center gap-4"
      >
        <div className="flex-1">
          <div className="text-emerald-200/70 text-[10px] uppercase tracking-[0.22em]">Recovery Score</div>
          <div className="text-3xl text-emerald-50/95 font-light mt-1">
            {recovery.value === "—" ? "—" : recovery.value}
            <span className="text-white/40 text-sm font-light"> / 10</span>
          </div>
          <div className="text-white/45 text-[11px] mt-0.5 italic">
            {recovery.logged_today
              ? `You logged ${recovery.today_score}/10 this morning. Tap to update.`
              : "How restored do you feel this morning? Tap to log."}
          </div>
        </div>
        <Sparkles size={20} className="text-emerald-300/70 flex-shrink-0" />
      </button>

      <div className="grid grid-cols-2 gap-3">
        {["bedtime_consistency", "morning_light_consistency", "evening_routine_consistency", "streak"].map((k) => {
          const t = tiles[k]; if (!t) return null;
          return (
            <div key={k} className="rounded-xl bg-white/[0.025] ring-1 ring-white/8 p-4">
              <div className="text-white/45 text-[10px] uppercase tracking-wider">{t.label}</div>
              <div className="text-3xl text-white/95 mt-1.5 font-light">{t.value}</div>
              <div className="text-white/45 text-[11px] mt-1">{t.unit}</div>
            </div>
          );
        })}
      </div>
      <div className="text-center text-white/40 text-xs pt-3 italic">
        Most people already know what to do. Reclaim helps you actually do it.
      </div>

      {rating && (
        <RecoveryScoreModal
          current={recovery.today_score}
          onClose={() => setRating(false)}
          onSaved={() => { setRating(false); refresh(); }}
        />
      )}
    </div>
  );
}

function RecoveryScoreModal({ current, onClose, onSaved }) {
  const [score, setScore] = useState(current || 7);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/biohacking/sleep/recovery-score`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ score }),
      });
      onSaved();
    } catch (_) { onSaved(); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-5" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-[#08101a] ring-1 ring-emerald-400/25 p-6" data-testid="sleep-recovery-modal">
        <div className="text-emerald-200/70 text-[10px] uppercase tracking-[0.3em] mb-2">Recovery Score</div>
        <h3 className="text-2xl text-emerald-50/95 font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
          How restored do you feel this morning?
        </h3>
        <p className="text-white/45 text-xs italic mt-2 mb-5">
          Tune in to the body, not the wearable. 1 = wrecked · 10 = radiant.
        </p>
        <div className="flex items-center justify-between gap-1 mb-4" data-testid="sleep-recovery-scale">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <button
              key={n}
              onClick={() => setScore(n)}
              data-testid={`sleep-recovery-${n}`}
              className={`w-8 h-10 rounded-md text-sm font-light transition ${
                score === n
                  ? "bg-emerald-400 text-[#02050B]"
                  : "bg-white/[0.04] text-white/65 hover:bg-white/[0.08]"
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg ring-1 ring-white/15 text-white/65 text-sm hover:bg-white/[0.04]">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            data-testid="sleep-recovery-save"
            className="flex-1 py-2.5 rounded-lg bg-emerald-400/90 hover:bg-emerald-400 text-[#02050B] text-sm font-medium disabled:opacity-60"
          >
            {saving ? "Saving…" : `Log ${score}/10`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Protocol ────────────────────────────────────────────────
function ProtocolPanel() {
  const [proto, setProto] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/protocol`).then((r) => r.json()).then((d) => setProto(d.protocol)).catch(() => {});
  }, []);
  if (!proto) return <Skel />;
  return (
    <div className="space-y-5">
      {["morning", "daytime", "evening", "bedroom"].map((bk) => {
        const block = proto[bk]; const Icon = BLOCK_ICON[bk] || Sun;
        return (
          <div key={bk} className="rounded-xl bg-white/[0.025] ring-1 ring-white/8 p-5" data-testid={`sleep-protocol-${bk}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={18} className="text-indigo-300" />
              <h3 className="text-lg" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>{block.title}</h3>
            </div>
            <div className="text-white/45 text-xs mb-4 italic">{block.tagline}</div>
            <div className="space-y-3.5">
              {block.items.map((it) => (
                <div key={it.key} className="border-l-2 border-indigo-500/30 pl-3">
                  <div className="text-white/90 text-sm">{it.title}</div>
                  <div className="text-white/45 text-xs mt-0.5">{it.why}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Checklist (Today's Routine + Sleep Environment merged) ──
// Single source of truth for everything-the-user-toggles in Sleep.
// Two distinct sections inside one panel so members don't have to
// switch tabs (and so we never ask the same question twice — the
// duplicated `bedroom_cooled` was removed from the daily checklist
// in favour of the persistent `bedroom_cool` in the environment
// scorecard).
function ChecklistPanel() {
  const [data, setData]   = useState(null);   // daily routine
  const [env, setEnv]     = useState(null);   // persistent environment

  const loadAll = useCallback(() => {
    fetch(`${API}/api/biohacking/sleep/checklist/today`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setData).catch(() => {});
    fetch(`${API}/api/biohacking/sleep/environment`, { headers: auth() })
      .then((r) => r.ok ? r.json() : null).then(setEnv).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Toggles ──────────────────────────────────────────────────
  const toggleRoutine = async (key, checked) => {
    setData((d) => d ? { ...d, items: d.items.map((it) => it.key === key ? { ...it, checked } : it) } : d);
    try {
      await fetch(`${API}/api/biohacking/sleep/checklist/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ key, checked }),
      });
      loadAll();
    } catch (_) { loadAll(); }
  };

  const toggleEnv = async (key, checked) => {
    setEnv((e) => e ? { ...e, items: e.items.map((it) => it.key === key ? { ...it, checked } : it) } : e);
    try {
      await fetch(`${API}/api/biohacking/sleep/environment/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ key, checked }),
      });
      loadAll();
    } catch (_) { loadAll(); }
  };

  if (!data || !env) return <Skel />;

  return (
    <div className="space-y-7">
      {/* ── Combined progress card ─────────────────────────── */}
      <div className="rounded-xl bg-indigo-500/10 ring-1 ring-indigo-400/25 p-4" data-testid="sleep-checklist-progress">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-white/55 text-[10px] uppercase tracking-wider">Today</div>
            <div className="text-white/95 text-lg mt-0.5">{data.completed} of {data.total} routine</div>
          </div>
          <div className="text-3xl text-indigo-200 font-light">{Math.round(data.completion_ratio * 100)}%</div>
        </div>
        <div className="mt-3 pt-3 border-t border-white/8 flex items-center justify-between gap-4">
          <div>
            <div className="text-white/55 text-[10px] uppercase tracking-wider">Environment</div>
            <div className="text-white/95 text-sm mt-0.5">{env.score} of {env.out_of} dialled in</div>
          </div>
          <div className="text-2xl text-emerald-200 font-light">{env.score}<span className="text-white/30 text-sm">/{env.out_of}</span></div>
        </div>
      </div>

      {/* ── Today's Routine (daily — resets each day) ──────── */}
      <section>
        <header className="flex items-baseline justify-between mb-2">
          <h3 className="text-indigo-200/85 text-[11px] uppercase tracking-[0.22em]">Today&apos;s Routine</h3>
          <span className="text-white/35 text-[10px] italic">resets daily</span>
        </header>
        <div className="space-y-1.5" data-testid="sleep-checklist-list">
          {data.items.map((it) => (
            <button
              key={it.key}
              onClick={() => toggleRoutine(it.key, !it.checked)}
              data-testid={`sleep-check-${it.key}`}
              className={`w-full flex items-center gap-3 p-3.5 rounded-lg ring-1 transition text-left ${
                it.checked ? "bg-emerald-500/10 ring-emerald-400/30" : "bg-white/[0.025] ring-white/8 hover:ring-white/15"
              }`}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                it.checked ? "bg-emerald-400 border-emerald-400" : "border-white/25"
              }`}>
                {it.checked && <Check size={14} className="text-[#02050B]" />}
              </div>
              <span className={`text-sm ${it.checked ? "text-emerald-100" : "text-white/85"}`}>{it.label}</span>
              <span className="ml-auto text-white/30 text-[10px] uppercase tracking-wider">{it.block}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Sleep Environment (persistent — toggle once) ───── */}
      <section data-testid="sleep-environment-section">
        <header className="flex items-baseline justify-between mb-2">
          <h3 className="text-emerald-200/85 text-[11px] uppercase tracking-[0.22em]">Sleep Environment</h3>
          <span className="text-white/35 text-[10px] italic">set once · tangible</span>
        </header>
        <div className="space-y-1.5">
          {env.items.map((it) => (
            <button
              key={it.key}
              onClick={() => toggleEnv(it.key, !it.checked)}
              data-testid={`sleep-env-${it.key}`}
              className={`w-full flex items-start gap-3 p-3.5 rounded-lg ring-1 transition text-left ${
                it.checked ? "bg-emerald-500/10 ring-emerald-400/30" : "bg-white/[0.025] ring-white/8 hover:ring-white/15"
              }`}
            >
              <div className={`w-5 h-5 mt-0.5 rounded border flex items-center justify-center flex-shrink-0 ${
                it.checked ? "bg-emerald-400 border-emerald-400" : "border-white/25"
              }`}>
                {it.checked && <Check size={14} className="text-[#02050B]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${it.checked ? "text-emerald-100" : "text-white/85"}`}>{it.label}</div>
                <div className="text-white/45 text-[11px] mt-0.5">{it.hint}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Tonight's tally · warm encouragement footer ─────
          Calm, never grade-like. Phrasing scales with how much is
          dialled in. Goal: emotional warmth on an otherwise
          analytical page. */}
      <TonightsTally
        dialled={data.completed + env.score}
        total={data.total + env.out_of}
      />
    </div>
  );
}

function TonightsTally({ dialled, total }) {
  // Tiered encouragement — calm phrasing, no grade language.
  let tone;
  if (total === 0) tone = "Tonight is a fresh canvas.";
  else if (dialled === 0)             tone = "Tomorrow is a fresh dial.";
  else if (dialled <= total * 0.25)   tone = "A start. The day softens from here.";
  else if (dialled <= total * 0.55)   tone = "Coming together. Stay with it.";
  else if (dialled <= total * 0.80)   tone = "Solid rhythm tonight.";
  else if (dialled < total)           tone = "Beautiful — almost full alignment.";
  else                                tone = "Every dial turned. Sleep deeply.";
  return (
    <div
      data-testid="sleep-tonights-tally"
      className="rounded-xl bg-gradient-to-br from-indigo-500/8 via-emerald-500/5 to-transparent ring-1 ring-white/8 p-4 text-center"
    >
      <div className="text-white/45 text-[10px] uppercase tracking-[0.28em]">Tonight</div>
      <div className="mt-1 text-2xl font-light text-white/90" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
        {dialled} of {total} dialled in
      </div>
      <p className="mt-1 text-white/55 text-xs italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
        {tone}
      </p>
    </div>
  );
}

// ── Installations ───────────────────────────────────────────
function InstallationsPanel() {
  const [list, setList] = useState(null);
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/installations`).then((r) => r.json()).then((d) => setList(d.installations || [])).catch(() => {});
  }, []);
  if (!list) return <Skel />;
  if (list.length === 0) return <div className="text-white/55 text-sm text-center py-8">Installations rendering — check back in a minute.</div>;
  return (
    <div className="space-y-3">
      {list.map((it) => (
        <div key={it.slug} className="rounded-xl bg-white/[0.025] ring-1 ring-white/8 p-5" data-testid={`sleep-install-${it.slug}`}>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-indigo-500/15 p-2 text-indigo-300 ring-1 ring-indigo-400/25"><Sparkles size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="text-white/95 text-base" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>{it.title}</div>
              <div className="text-white/50 text-xs italic mt-0.5">{it.subtitle}</div>
              <div className="text-white/35 text-[11px] mt-2">{Math.round((it.duration_s || 0) / 60)} min · your voice · soft healing music</div>
            </div>
          </div>
          <SleepAudioPlayer src={`${API}${it.audio_url}`} playing={playing === it.slug} onToggle={() => setPlaying(playing === it.slug ? null : it.slug)} />
        </div>
      ))}
    </div>
  );
}

function SleepAudioPlayer({ src, playing, onToggle }) {
  const [audio] = useState(() => typeof Audio !== "undefined" ? new Audio() : null);
  useEffect(() => {
    if (!audio) return;
    audio.src = src;
    audio.preload = "none";
    audio.loop = false;
    return () => { audio.pause(); };
  }, [audio, src]);
  useEffect(() => {
    if (!audio) return;
    if (playing) audio.play().catch(() => {});
    else audio.pause();
  }, [audio, playing]);
  return (
    <button
      onClick={onToggle}
      className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-100 ring-1 ring-indigo-400/30 transition"
    >
      {playing ? <><Pause size={16} /> Pause</> : <><Play size={16} /> Play installation</>}
    </button>
  );
}

// ── Tools ───────────────────────────────────────────────────
function ToolsPanel() {
  const [tools, setTools] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/biohacking/sleep/tools`).then((r) => r.json()).then((d) => setTools(d.tools || [])).catch(() => {});
  }, []);
  if (!tools) return <Skel />;
  return (
    <div className="space-y-3">
      <div className="text-white/55 text-xs text-center pb-2 italic">Recommended tools — affiliate partnerships coming soon.</div>
      {tools.map((t) => (
        <div key={t.slug} className="flex items-center gap-3 rounded-xl bg-white/[0.025] ring-1 ring-white/8 p-4" data-testid={`sleep-tool-${t.slug}`}>
          <div className="rounded-full bg-amber-500/10 p-2 text-amber-300 ring-1 ring-amber-400/20"><ShoppingBag size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-white/90 text-sm">{t.name}</div>
            <div className="text-white/50 text-xs mt-0.5">{t.description}</div>
          </div>
          <div className="text-white/35 text-xs flex-shrink-0">{t.price_band}</div>
          <ChevronRight size={16} className="text-white/30 flex-shrink-0" />
        </div>
      ))}
    </div>
  );
}

function Skel() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="animate-spin text-white/30" size={24} />
    </div>
  );
}
